(function() {
	const loadScript = (id, src) => {
		const existing = document.getElementById(id);

		if (existing) {
			return Promise.resolve();
		}

		return new Promise((resolve, reject) => {
			const script = document.createElement("script");
			script.id = id;
			script.src = src;
			script.defer = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
			document.head.appendChild(script);
		});
	};

	const loadStylesheet = (id, href) => {
		const existing = document.getElementById(id);

		if (existing) {
			return;
		}

		const style = document.createElement("link");
		style.id = id;
		style.rel = "stylesheet";
		style.href = href;
		document.head.appendChild(style);
	};

	const getLargestImageSrc = (imageElement) => {
		if (!imageElement) {
			return "";
		}

		const srcset = imageElement.getAttribute("srcset") || "";

		if (srcset) {
			const candidate = srcset
				.split(",")
				.map((entry) => entry.trim())
				.map((entry) => {
					const match = entry.match(/^(\S+)\s+(\d+)w$/);

					if (!match) {
						return null;
					}

					return { url: match[1], width: parseInt(match[2], 10) };
				})
				.filter(Boolean)
				.sort((a, b) => b.width - a.width)[0];

			if (candidate && candidate.url) {
				return candidate.url;
			}
		}

		return imageElement.currentSrc || imageElement.src || "";
	};

	class ProductMediaModulobox extends HTMLElement {
		static get observedAttributes() {
			return ["open"];
		}

		connectedCallback() {
			this.mediaElement = this.closest(".product__media");
			this.galleryName = this.id || `product-media-gallery-${Math.random().toString(36).slice(2)}`;
			this.hidden = true;
		}

		get open() {
			return this.hasAttribute("open");
		}

		set open(value) {
			this.toggleAttribute("open", value);
		}

		async attributeChangedCallback(name) {
			if (name !== "open" || !this.open) {
				return;
			}

			try {
				await this._openModulobox();
			} finally {
				this.removeAttribute("open");
			}
		}

		async _openModulobox() {
			const libs = window.themeVariables && window.themeVariables.libs ? window.themeVariables.libs : {};

			if (!libs.modulobox || !libs.moduloboxCss) {
				return;
			}

			loadStylesheet("modulobox-styles", libs.moduloboxCss);

			if (!window.ModuloBox) {
				await loadScript("modulobox-script", libs.modulobox);
			}

			if (!window.ModuloBox || !this.mediaElement) {
				return;
			}

			const mediaItems = this._buildMediaItems();

			if (!mediaItems.length) {
				return;
			}

			const selectedIndex = Math.max(0, mediaItems.findIndex((item) => item.selected));

			// Destroy any existing instance so the new one starts with a clean slate.
			// ModuloBox stores event handler bindings on the shared ThemeoneUtils singleton
			// (window.ThemeoneUtils.event_handlers). Without clearing that cache the new
			// instance's buttons would remain bound to the destroyed instance's methods,
			// making the lightbox appear open but completely unresponsive ("numb").
			if (window.__productMediaModulobox) {
				try {
					window.__productMediaModulobox.destroy();
				} catch (_) {}

				window.__productMediaModulobox = null;
				this.modulobox = null;
			}

			// Always clear shared event handler cache before creating a new instance.
			if (window.ThemeoneUtils && window.ThemeoneUtils.event_handlers) {
				delete window.ThemeoneUtils.event_handlers;
			}

			const mb = new window.ModuloBox({
				mediaSelector: ".__modulobox-product-media__",
				history: false,
				scrollBar: false,
				thumbnails: true,
				videoAutoPlay: true,
				controls: ["fullScreen", "zoom", "close"],
				minZoom: 1.01,
				maxZoom: 6
			});

			mb.init();

			if (typeof mb.on === "function") {
				const syncZoomControl = () => {
					const zoomButton = mb.buttons && mb.buttons.zoom;

					if (!zoomButton || typeof mb.getMedia !== "function") {
						return;
					}

					const currentMedia = mb.getMedia();
					zoomButton.style.display = currentMedia && currentMedia.type === "image" ? "" : "none";
				};

				mb.on("afterOpen.modulobox", syncZoomControl);
				mb.on("updateMedia.modulobox", syncZoomControl);
			}

			window.__productMediaModulobox = mb;
			this.modulobox = mb;

			mb.addMedia(this.galleryName, mediaItems);
			mb.open(this.galleryName, selectedIndex);
			this.removeAttribute("active-media-id");
		}

		_buildMediaItems() {
			const visibleItems = Array.from(this.mediaElement.querySelectorAll(".product__media-item:not(.is-filtered)"));
			const activeMediaId = this.getAttribute("active-media-id");

			if (!this.templatesHost) {
				this.templatesHost = document.createElement("div");
				this.templatesHost.hidden = true;
				this.appendChild(this.templatesHost);
			}

			this.templatesHost.innerHTML = "";

			return visibleItems
				.map((item) => {
					const mediaType = item.getAttribute("data-media-type");
					const mediaId = item.getAttribute("data-media-id");
					const preview = item.querySelector("img");
					const thumbnailImage = mediaId
						? this.mediaElement.querySelector(`.product__thumbnail-item[data-media-id="${mediaId}"] img`)
						: null;
					const sourceTemplate = item.querySelector("template");
					const thumbSrc = thumbnailImage
						? thumbnailImage.currentSrc || thumbnailImage.src
						: (preview ? preview.currentSrc || preview.src : "");
					const selected = activeMediaId ? mediaId === activeMediaId : item.classList.contains("is-selected");
					const caption = preview ? preview.getAttribute("alt") || "" : "";

					if (mediaType === "image") {
						const fullImage = item.querySelector(".product__media-image-wrapper img") || preview;

						if (!fullImage) {
							return null;
						}

						return {
							type: "image",
							src: getLargestImageSrc(fullImage),
							thumb: thumbSrc,
							caption: fullImage.getAttribute("alt") || "",
							selected: selected
						};
					}

					if (mediaType === "video") {
						const templateContent = sourceTemplate ? sourceTemplate.content : null;
						const sourceNodes = templateContent
							? Array.from(templateContent.querySelectorAll("video source[src]"))
							: Array.from(item.querySelectorAll("video source[src]"));
						const videoNode = templateContent
							? templateContent.querySelector("video[src]")
							: item.querySelector("video[src]");
						const sourceList = sourceNodes.map((sourceNode) => sourceNode.getAttribute("src")).filter(Boolean);
						const primarySrc = videoNode ? videoNode.getAttribute("src") : null;
						const posterImage = preview ? preview.currentSrc || preview.src : "";
						const finalSrcList = sourceList.length ? sourceList : (primarySrc ? [primarySrc] : []);

						if (finalSrcList.length) {
							return {
								type: "video",
								format: "html5",
								src: finalSrcList.join(","),
								poster: posterImage,
								thumb: thumbSrc,
								caption: caption,
								selected: selected
							};
						}
					}

					if (mediaType === "external_video") {
						const templateContent = sourceTemplate ? sourceTemplate.content : null;
						const iframe = templateContent
							? templateContent.querySelector("iframe[src]")
							: item.querySelector("external-video iframe[src], iframe[src]");
						let iframeSrc = iframe ? iframe.getAttribute("src") : "";

						if (!iframeSrc) {
							const htmlSource = sourceTemplate ? sourceTemplate.innerHTML : item.innerHTML;
							const srcMatch = htmlSource ? htmlSource.match(/https?:\/\/[^"'\s>]+/i) : null;

							if (srcMatch && srcMatch[0]) {
								iframeSrc = srcMatch[0].replace(/&amp;/g, "&");
							}
						}

						if (iframeSrc) {
							return {
								type: "iframe",
								src: iframeSrc,
								thumb: thumbSrc,
								caption: caption,
								selected: selected
							};
						}
					}

					if (!mediaId) {
						return null;
					}

					const templateId = `${this.galleryName}-media-${mediaId}`;
					const templateNode = document.createElement("div");
					templateNode.id = templateId;
					templateNode.innerHTML = item.innerHTML;
					this.templatesHost.appendChild(templateNode);

					return {
						type: "HTML",
						src: `#${templateId}`,
						thumb: thumbSrc,
						caption: caption,
						selected: selected
					};
				})
				.filter(Boolean);
		}
	}

	if (!window.customElements.get("product-media-modulobox")) {
		window.customElements.define("product-media-modulobox", ProductMediaModulobox);
	}
})();
